
import { TestResult } from "@/hooks/useTestResults";

export interface CategoryCompletion {
  category: string;
  completed: boolean;
  totalTests: number;
  completedTests: number;
  averageScore?: number;
}

export interface GradeLevelCompletion {
  gradeLevel: string;
  completed: boolean;
  totalTests: number;
  completedTests: number;
  averageScore?: number;
  categories: CategoryCompletion[];
}

export const gradeLevelOrder = [
  'Infant',
  'Age 1',
  'Age 2',
  'Age 3',
  'Age 4',
  'Age 5',
  'Age 6'
];

export const formatLevelTitle = (level: string): string => {
  return level;
};

export const processTestResults = (
  availableTests: any[],
  testResults: TestResult[]
): Record<string, GradeLevelCompletion> => {
  const gradeLevelCompletions: Record<string, GradeLevelCompletion> = {};

  // Initialize grade level completions with available tests
  availableTests.forEach(test => {
    if (!gradeLevelCompletions[test.grade_level]) {
      gradeLevelCompletions[test.grade_level] = {
        gradeLevel: test.grade_level,
        completed: false,
        totalTests: 0,
        completedTests: 0,
        categories: [],
      };
    }

    const gradeLevel = gradeLevelCompletions[test.grade_level];
    gradeLevel.totalTests++;

    let category = gradeLevel.categories.find(c => c.category === test.category);
    if (!category) {
      category = {
        category: test.category,
        completed: false,
        totalTests: 0,
        completedTests: 0,
      };
      gradeLevel.categories.push(category);
    }
    category.totalTests++;
  });

  // Get unique completed test IDs and organize test results by ID for score lookup
  const completedTestIds = new Set(testResults.map(r => r.test_id));
  const testScores = new Map(testResults.map(r => [r.test_id, r.score]));

  // Update completion status and calculate average scores
  Object.values(gradeLevelCompletions).forEach(gradeLevel => {
    const gradeLevelTests = availableTests.filter(t => t.grade_level === gradeLevel.gradeLevel);
    const completedGradeLevelTests = gradeLevelTests.filter(t => completedTestIds.has(t.id));
    gradeLevel.completedTests = completedGradeLevelTests.length;
    gradeLevel.completed = gradeLevel.completedTests === gradeLevel.totalTests;

    // Calculate average score for grade level
    if (gradeLevel.completed) {
      const scores = completedGradeLevelTests
        .map(t => testScores.get(t.id))
        .filter((score): score is number => score !== undefined);
      gradeLevel.averageScore = Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
    }

    gradeLevel.categories.forEach(category => {
      const categoryTests = gradeLevelTests.filter(t => t.category === category.category);
      const completedCategoryTests = categoryTests.filter(t => completedTestIds.has(t.id));
      category.completedTests = completedCategoryTests.length;
      category.completed = category.completedTests === category.totalTests;

      // Calculate average score for category
      if (category.completed) {
        const scores = completedCategoryTests
          .map(t => testScores.get(t.id))
          .filter((score): score is number => score !== undefined);
        category.averageScore = Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
      }
    });
  });

  return gradeLevelCompletions;
};
